import React, { useState, useRef, useCallback, Fragment } from 'react';
import { LoadScript, GoogleMap, PolygonF, MarkerF, CircleF } from '@react-google-maps/api';

import './Geofence.scss';

interface LatLng {
    lat: number;
    lng: number;
}

interface GeoFenceProps {
    latitude: number;
    longitude: number;
    radius: number;
    geofenceType: string;
    selectedPolygonCoordinates?: LatLng[];
    onEditMap: (polygonCoordinates: LatLng[]) => void;
    onCircleDrag: (newCoords: LatLng) => void;
    onEditCircleRadius: (newRadius: number) => void;
    onChangeWellSiteLatLng?: (lat: number, lng: number) => void
    onChangeStagingLatLng?: (lat: number, lng: number) => void
}
const defaultGeoFenceRadius = 300;

const GeofenceMap = (props: GeoFenceProps): React.ReactElement => {
    const [path, setPath] = useState<LatLng[]>([
        { lat: props.latitude, lng: props.longitude },
        { lat: props.latitude, lng: props.longitude },
    ]);
    const [radius, setRadius] = useState(0);
    const [latitude, setLatitude] = useState(props.latitude ??  0);
    const [longitude, setLongitude] = useState(props.longitude ?? 0);

    const [selectedPolygonCoordinates, setSelectedpolygonCoordinates] = useState<LatLng[]>([]);

    const polygonRef = useRef<google.maps.Polygon | null>(null);
    const listenersRef = useRef<google.maps.MapsEventListener[]>([]);
    const circleRef = useRef<google.maps.Circle | null>(null);

    const onEdit = useCallback(() => {
        if (polygonRef.current) {
            const nextPath = polygonRef.current
                .getPath()
                .getArray()
                .map((latLng: google.maps.LatLng) => {
                    return { lat: latLng.lat(), lng: latLng.lng() };
                });
            console.info(nextPath);
            setSelectedpolygonCoordinates(nextPath);
            props.onEditMap(nextPath);
        }
    }, [setSelectedpolygonCoordinates]);

    const onLoad = useCallback(
        (polygon: google.maps.Polygon) => {
            polygonRef.current = polygon;
            const path = polygon.getPath();
            listenersRef.current.push(
                path.addListener('set_at', onEdit),
                path.addListener('insert_at', onEdit),
                path.addListener('remove_at', onEdit),
            );
        },
        [onEdit],
    );

    const changedRadius = () => {
        if (circleRef.current) {
            const radius = circleRef.current.getRadius();
            setRadius(Math.round(radius));
            props.onEditCircleRadius(radius);
        }
    };

    const onCircleDragEnd = e => {
        props.onCircleDrag(e.latLng);
        setLatitude(e.latLng.lat());
        setLongitude(e.latLng.lng());
        updateParentLatLng(e.latLng.lat(), e.latLng.lng());
    };

    const onUnmount = useCallback(() => {
        listenersRef.current.forEach(lis => lis.remove());
        polygonRef.current = null;
        circleRef.current = null;
    }, []);

    const onCircleLoad = useCallback(
        (circle: google.maps.Circle) => {
            circleRef.current = circle;
        },
        [changedRadius, onCircleDragEnd],
    );

    const handleMarkerMove = (event) => {
        const lat: number = event.latLng.lat();
        const lng: number = event.latLng.lng();
        setLatitude(lat);
        setLongitude(lng);
        updateParentLatLng(lat, lng);
    }

    // this will update the lat and lng text fields
    const updateParentLatLng = (lat: number, lng:number) => {
        if (props.onChangeWellSiteLatLng) {
            props.onChangeWellSiteLatLng(lat, lng);
        }
        if (props.onChangeStagingLatLng) {
            props.onChangeStagingLatLng(lat, lng);
        }
    }

    return (
        <div className="GeoFenceMap-Container">
            <LoadScript googleMapsApiKey={import.meta.env.VITE_GOOGLE_MAPS_API_KEY || ""}>
            {/* <LoadScript googleMapsApiKey=""> */}
                <GoogleMap
                    mapContainerClassName="GeoFence-Map"
                    center={{ lat: props.latitude, lng: props.longitude }}
                    zoom={16}
                    mapTypeId="satellite"
                >
                    <PolygonF
                        editable
                        draggable
                        path={path}
                        paths={props.selectedPolygonCoordinates}
                        onMouseUp={onEdit}
                        onDragEnd={onEdit}
                        onLoad={onLoad}
                        onUnmount={onUnmount}
                        options={{
                            fillColor: '#009dc7',
                            strokeColor: '#009dc7',
                            strokeOpacity: 0.7,
                            fillOpacity: 0.3,
                            strokeWeight: 2,
                        }}
                        visible={props.geofenceType !== 'Circle'}
                    />
                    <Fragment>
                        <MarkerF
                            onDragEnd={handleMarkerMove}
                            draggable
                            position={{
                                lat: latitude,
                                lng: longitude,
                            }}
                        />
                        <CircleF
                            draggable
                            editable
                            onDragEnd={onCircleDragEnd}
                            onRadiusChanged={() => {
                                changedRadius();
                            }}
                            onUnmount={onUnmount}
                            onLoad={onCircleLoad}
                            center={{
                                lat: latitude != 0 ? latitude : parseFloat(props.latitude.toFixed(6)),
                                lng: longitude != 0 ? longitude : parseFloat(props.longitude.toFixed(6)),
                            }}
                            radius={props.radius ? parseInt(props.radius.toString()) : defaultGeoFenceRadius}
                            options={{
                                fillColor: '#009dc7',
                                strokeColor: '#009dc7',
                                strokeOpacity: 0.7,
                                fillOpacity: 0.3,
                                strokeWeight: 1,
                            }}
                            visible={props.geofenceType !== 'Polygon'}
                        />
                    </Fragment>
                </GoogleMap>
            </LoadScript>
        </div>
    );
};

export default GeofenceMap;
